import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { MessageCircle, Send, X, Sparkles, Loader2, MoveVertical, Move, ArrowRight } from "lucide-react";

const POSITIONS = {
  "bottom-right": { className: "bottom-6 right-6" },
  "bottom-left":  { className: "bottom-6 left-6"  },
  "top-right":    { className: "top-6 right-6"    },
  "top-left":     { className: "top-6 left-6"     },
};
const POSITION_ORDER = ["bottom-right", "bottom-left", "top-right", "top-left"];
const POSITION_LABEL = {
  "bottom-right": "Bottom right",
  "bottom-left":  "Bottom left",
  "top-right":    "Top right",
  "top-left":     "Top left",
};

const SEED_PROMPTS = [
  "How do I set up the Auto-Agent?",
  "How many credits does a proposal cost?",
  "How do I add past performance?",
  "What is Market Benchmark?",
];

const INITIAL = { role: "assistant", content: "Hi! I'm the BidForge help assistant. Ask me anything about credits, Auto-Agent setup, proposals, or any tab in the sidebar." };

/**
 * Floating AI help chatbot. Draggable via the position toggle so it never
 * covers a workflow. Persists position + open-state in localStorage.
 * Free — does NOT consume workspace credits.
 */
export default function HelpChatbot() {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(() => localStorage.getItem("bf_help_pos") || "bottom-right");
  const [messages, setMessages] = useState([INITIAL]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => { localStorage.setItem("bf_help_pos", position); }, [position]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = async (msg) => {
    const text = (msg ?? draft).trim();
    if (!text || busy) return;
    setDraft("");
    const next = [...messages, { role: "user", content: text }];
    setMessages(next);
    setBusy(true);
    try {
      const history = next.slice(1, -1); // drop seed greeting + current
      const r = await api.post("/help/chat", { message: text, history });
      setMessages((m) => [...m, { role: "assistant", content: r.data.answer, action: r.data.action || null }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry — I hit an error. Try again in a moment, or email support@bidforgeai.org." }]);
    } finally { setBusy(false); }
  };

  const runAction = (action) => {
    if (!action?.path) return;
    setOpen(false);
    navigate(action.path);
  };

  const cyclePosition = () => {
    const idx = POSITION_ORDER.indexOf(position);
    setPosition(POSITION_ORDER[(idx + 1) % POSITION_ORDER.length]);
  };

  const anchor = POSITIONS[position]?.className || POSITIONS["bottom-right"].className;
  const isBottom = position.startsWith("bottom");
  const panelAnchor = isBottom
    ? `${anchor.replace(/(top|bottom)-6/, "$1-24")}`
    : `${anchor.replace(/(top|bottom)-6/, "$1-24")}`;

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className={`fixed ${anchor} z-50 flex items-center gap-2 px-4 py-3 rounded-full text-white shadow-lg hover:shadow-xl transition-all bf-pill`}
        data-testid="help-chatbot-fab"
        aria-label="Open help assistant"
      >
        <Sparkles className="w-4 h-4" />
        <span className="text-sm font-bold tracking-tight">Ask BidForge</span>
      </button>
    );
  }

  return (
    <div
      className={`fixed ${anchor} z-50 w-[380px] max-w-[calc(100vw-3rem)] rounded-2xl bg-white border border-slate-200 shadow-2xl flex flex-col`}
      style={{ height: "min(560px, calc(100vh - 6rem))" }}
      data-testid="help-chatbot-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-violet-50 to-fuchsia-50 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white">
            <Sparkles className="w-4 h-4" />
          </div>
          <div>
            <div className="font-display font-bold text-sm text-slate-900">BidForge Help</div>
            <div className="text-[10px] font-mono font-bold tracking-widest text-violet-600">FREE · NO CREDITS</div>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={cyclePosition}
            className="w-8 h-8 rounded-lg hover:bg-white/70 flex items-center justify-center text-slate-500 hover:text-violet-600 transition-colors"
            title={`Move window (currently ${POSITION_LABEL[position]})`}
            data-testid="help-chatbot-move"
          >
            <Move className="w-4 h-4" />
          </button>
          <button
            onClick={() => setOpen(false)}
            className="w-8 h-8 rounded-lg hover:bg-white/70 flex items-center justify-center text-slate-500 hover:text-rose-600 transition-colors"
            data-testid="help-chatbot-close"
            aria-label="Close help"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] ${m.role === "user" ? "" : "space-y-2"}`}>
              <div
                className={`px-3 py-2 rounded-2xl whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white rounded-br-sm"
                    : "bg-slate-100 text-slate-800 rounded-bl-sm"
                }`}
                data-testid={`help-msg-${m.role}-${i}`}
              >
                {m.content}
              </div>
              {m.action && (
                <button
                  onClick={() => runAction(m.action)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-violet-300 text-violet-700 hover:bg-violet-50 text-xs font-mono font-bold tracking-widest transition-colors"
                  data-testid={`help-action-${i}`}
                >
                  {m.action.label}
                  <ArrowRight className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>
        ))}
        {busy && (
          <div className="flex justify-start">
            <div className="max-w-[85%] px-3 py-2 rounded-2xl bg-slate-100 text-slate-500 rounded-bl-sm flex items-center gap-2" data-testid="help-thinking">
              <Loader2 className="w-3 h-3 animate-spin" />
              <span className="text-xs">Thinking…</span>
            </div>
          </div>
        )}
        {messages.length === 1 && !busy && (
          <div className="pt-2 space-y-1.5" data-testid="help-seed-prompts">
            <div className="text-[10px] font-mono font-bold tracking-widest text-slate-400 mb-1">TRY ASKING</div>
            {SEED_PROMPTS.map((p) => (
              <button
                key={p}
                onClick={() => send(p)}
                className="block w-full text-left px-3 py-2 rounded-lg text-xs text-slate-700 bg-slate-50 hover:bg-violet-50 hover:text-violet-700 border border-slate-200 hover:border-violet-200 transition-colors"
                data-testid={`help-seed-${p.slice(0, 12).replace(/\W/g, '-').toLowerCase()}`}
              >
                {p}
              </button>
            ))}
          </div>
        )}
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); send(); }}
        className="border-t border-slate-100 p-3 flex items-center gap-2"
      >
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask about credits, Auto-Agent, proposals…"
          className="flex-1 rounded-full px-4 py-2 bg-slate-50 border border-slate-200 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none text-sm text-slate-900"
          data-testid="help-chatbot-input"
          disabled={busy}
        />
        <button
          type="submit"
          disabled={busy || !draft.trim()}
          className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white hover:brightness-110 flex items-center justify-center disabled:opacity-50 transition-all"
          data-testid="help-chatbot-send"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
}
